import type { AttachedDoc } from "../db/types";
import type { Config } from "../state/config";
import { authFetch } from "./authFetch";

// Pages web citées dans la question : le serveur les récupère (POST /fetch-url,
// server/app/url_fetch.py) et elles rejoignent la question comme un document
// glissé dans le chat. Sans ça, le modèle ne voyait que des extraits de
// recherche et ne pouvait pas, par exemple, rendre une page en version
// copiable (retour de la DPO, 19.10.2026).

/** Au-delà, une question qui colle une liste de liens enverrait des dizaines
 *  de pages au modèle. */
export const MAX_PAGES = 3;
/** ≈ 30 k tokens par page : de quoi tenir une page de règlement entière. */
const MAX_PAGE_CHARS = 120_000;

export interface WebPage {
  url: string;
  title: string;
  markdown: string;
}

/** Les URL http(s) de la question, sans la ponctuation qui les suit dans une
 *  phrase, sans doublon, dans l'ordre. */
export function extractUrls(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/https?:\/\/[^\s<>"'«»]+/g)) {
    let url = m[0].replace(/[.,;:!?]+$/, "");
    // Une parenthèse fermante n'appartient à l'URL que si elle en ouvre une.
    while (url.endsWith(")") && (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0)) {
      url = url.slice(0, -1);
    }
    if (url.endsWith(">") || url.endsWith("]")) url = url.slice(0, -1);
    if (!out.includes(url)) out.push(url);
  }
  return out.slice(0, MAX_PAGES);
}

export async function fetchWebPage(url: string, cfg: Config, signal?: AbortSignal): Promise<WebPage> {
  const res = await authFetch(`${cfg.serverUrl.replace(/\/$/, "")}/fetch-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
    signal,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { detail?: string }).detail ?? "";
    } catch {
      /* corps non JSON */
    }
    throw new Error(detail || `erreur ${res.status}`);
  }
  return (await res.json()) as WebPage;
}

/** La page, prête à partir avec la question. */
export function pageToDoc(page: WebPage): AttachedDoc {
  let text = page.markdown;
  if (text.length > MAX_PAGE_CHARS) {
    text = `${text.slice(0, MAX_PAGE_CHARS)}\n\n[… page tronquée : seul le début a été transmis]`;
  }
  text = `Source : ${page.url}\n\n${text}`;
  return { name: `Page web : ${page.title}`, text, tokens: Math.round(text.length / 4) };
}

/** Une page qu'on n'a pas pu lire : le modèle doit le dire plutôt que
 *  d'inventer son contenu. */
export function unreadablePageDoc(url: string, reason: string): AttachedDoc {
  const text = `La page ${url} n'a pas pu être lue (${reason}). Dis-le à l'usager et n'invente pas son contenu.`;
  return { name: `Page web illisible : ${url}`, text, tokens: Math.round(text.length / 4) };
}
